import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlaylistsAPI } from "../../api/api";
import FolderTree from "./FolderTree"
import "./Playlists.css"

export default function Playlists() {
    const [playlists, setPlaylists] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [showAddFolderModal, setShowAddFolderModal] = useState(false);
    const [showAddFileModal, setShowAddFileModal] = useState(false);
    const [activePlaylist, setActivePlaylist] = useState(null);
    const [folderData, setFolderData] = useState({});
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const loadPlaylists = async () => {
        try {
            setLoading(true);
            const data = await PlaylistsAPI.getAll();
            const list = Array.isArray(data.data) ? data.data : [];

            setPlaylists(list);

            if (list.length > 0) {
                const smallest = list.reduce((prev, curr) =>
                    curr.id < prev.id ? curr : prev
                );

                if (!activePlaylist) {
                    setActivePlaylist(smallest);
                    loadTracks(smallest);
                } else {
                    loadTracks(activePlaylist);
                }
            }
        } catch (err) {
            console.error("Failed to load playlists:", err);
            setPlaylists([]);
        } finally {
            setLoading(false);
        }
    };

    const loadTracks = async (playlist) => {
        setTracks(Array.isArray(playlist?.tracks) ? playlist?.tracks : [])
    };

    useEffect(() => {
        loadPlaylists();
    }, []);

    const handleSettings = () => {
        navigate(`/playlist/settings`);
    }

    const handleDelete = async (id, activePlaylist) => {
        if (!id) return;

        try {
            setLoading(true);
            await PlaylistsAPI.removeTrack(id);
            loadPlaylists();
        } catch (err) {
            console.error("Failed to remove track:", err);
        } finally {
            setLoading(false);
            setActivePlaylist(activePlaylist)
        }
    }

    const handleEdit = () => {

    }

    const handleAddFolder = async () => {
        if (!selectedFolder) return;

        const payload = { folderName: selectedFolder.path, playlistId: activePlaylist.id }

        try {
            setLoading(true);
            await PlaylistsAPI.addFolder(payload);
            setShowAddFolderModal(false);
            setActivePlaylist(activePlaylist);
            setSelectedFolder(null);
            loadPlaylists();
            loadTracks(activePlaylist);
        } catch (err) {
            console.error("Failed to add folder:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFolder = async () => {
        try {
            setLoading(true);
            const data = await PlaylistsAPI.browse();
            setFolderData(data.data);
        } catch (err) {
            console.error("Failed to load playlists:", err);
            setFolderData([]);
        } finally {
            setLoading(false);
        }
    }

    const handleBrowseFile = async () => {
        try {
            setLoading(true);
            const data = await PlaylistsAPI.browse();
            setFolderData(data.data);
        } catch (err) {
            console.error("Failed to browse:", err);
        } finally {
            setLoading(false);
        }
    }

    const openAddFileModal = async () => {
        setShowAddFileModal(true);
        await handleBrowseFile();
    };

    const handleAddFile = async () => {
        if (!selectedFile) return;

        const payload = { PlaylistId: activePlaylist.id, FilePath: selectedFile.path, FileName: selectedFile.name, }

        try {
            setLoading(true);
            await PlaylistsAPI.addFile(payload);
            setShowAddFileModal(false);
            setActivePlaylist(activePlaylist);
            setSelectedFile(null);
            loadPlaylists();
        } catch (err) {
            console.error("Failed to add file:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabClick = (playlist) => {
        setActivePlaylist(playlist);
        loadTracks(playlist);
    };

    const folderModal = () => {
        return (
            <div className="playlists-modal-overlay">
                <div className="playlists-modal">
                    <h2>Select Folder</h2>
                    <div className="folder-tree-container">
                        {folderData ? (
                            <FolderTree
                                root={folderData}
                                selected={selectedFolder}
                                onSelect={setSelectedFolder}
                                mode="folders"
                            />
                        ) : (
                            <p>Loading folders...</p>
                        )}
                    </div>
                    <div className="playlists-modal-buttons">
                        <button
                            onClick={handleAddFolder}
                            className="playlists-create-btn"
                            disabled={!selectedFolder}
                        >
                            Add
                        </button>
                        <button
                            onClick={() => { setShowAddFolderModal(false); setSelectedFolder(null); setFolderData({}); setActivePlaylist(activePlaylist); }}
                            className="playlists-cancel-btn"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const fileModal = () => {
        return (
            <div className="playlists-modal-overlay">
                <div className="playlists-modal">
                    <h2>Select File</h2>

                    <div className="folder-tree-container">
                        {folderData ? (
                            <FolderTree
                                root={folderData}
                                selected={selectedFile}
                                onSelect={setSelectedFile}
                                mode="files"
                            />
                        ) : (
                            <p>Loading folders...</p>
                        )}
                    </div>

                    <div className="playlists-modal-buttons">
                        <button
                            onClick={handleAddFile}
                            className="playlists-create-btn"
                            disabled={!selectedFile}
                        >
                            Add
                        </button>
                        <button
                            onClick={() => {
                                setShowAddFileModal(false);
                                setSelectedFile(null);
                                setActivePlaylist(activePlaylist);
                            }}
                            className="playlists-cancel-btn"
                        >
                            Cancel
                        </button>
                    </div>
                </div>

            </div>
        );
    };

    const tableContainer = () => {
        return (
            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th className="p-id">#</th>
                            <th className="p-name">Title</th>
                            <th className="p-artist">Artist</th>
                            <th className="p-duration">Duration</th>
                            <th className="p-gain">Gain</th>
                            <th className="p-weight">Weight</th>
                            <th className="p-filename">File Name</th>
                            <th className="p-filePath">File Path</th>
                            <th className="p-btn"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tracks.length > 0 ? (
                            tracks.map((track, i) => (
                                <tr key={i}>
                                    <td className="p-id">{i + 1}</td>
                                    <td className="p-name" title={track.title}>{track.title}</td>
                                    <td className="p-artist" title={track.artist}>{track.artist}</td>
                                    <td className="p-duration" title={track.duration}>{track.duration}</td>
                                    <td className="p-gain" title={track.trackGain}>{track.trackGain}</td>
                                    <td className="p-weight" title={track.weight}>{track.weight}</td>
                                    <td className="p-filename" title={track.fileName}>{track.fileName}</td>
                                    <td className="p-filePath" title={track.filePath}>{track.filePath}</td>
                                    <td className="p-btn">
                                        <button
                                            onClick={() => handleEdit(track)}
                                            className="edit-btn"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(track.id, activePlaylist)}
                                            className="del-btn"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className="no-content" colSpan="7" rowSpan="3" style={{ textAlign: "center" }}>
                                    No tracks found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const tabsContainer = () => {
        return (
            <div className="playlists-tabs-container">
                <div className="playlist-tabs">
                    {playlists.map((playlist) => (
                        <div
                            key={playlist.id}
                            className={`tab ${activePlaylist?.id === playlist.id ? "active" : ""}`}
                            onClick={() => handleTabClick(playlist)}
                        >
                            <span>{playlist.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const mainButtonsView = () => {
        return (
            <div className="file-btn">
                <div className="playlists-pl-btn">
                    <button className="add-btn"
                        onClick={() => {
                            setShowAddFolderModal(true);
                            handleSelectFolder();
                        }}
                    >Add Folder
                    </button>
                </div>
                <div className="playlists-pl-btn">
                    <button className="add-btn"
                        onClick={() => openAddFileModal()}
                    >Add File
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="playlists-main">
            <div className="playlists-page-main">
                {loading
                    ? <div className="spinner"></div>
                    :
                    <>
                        <div className="playlists-main-btn"><button onClick={() => handleSettings()} className="pl-settings-btn" ><i className="fa-solid fa-gear"></i></button></div>

                        {activePlaylist && (
                            <div className="table-main">
                                <h1>{activePlaylist.name} Tracks</h1>
                                {mainButtonsView()}
                                {tabsContainer()}
                                {tableContainer()}
                            </div>
                        )
                        }
                    </>
                }
                {showAddFolderModal && folderModal()}
                {showAddFileModal && fileModal()}
            </div>
        </div>
    );
};